import { FC } from 'react'

import { Control, Controller, FieldErrors } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import clsx from 'clsx'
import dayjs from 'dayjs'

import { DATE_FORMAT_LIST } from 'shared/constants/dateFormatList'
import {
  NON_DIGITS_REGEX,
  REGEX_EMAIL_NUMBER,
  REGEX_NAME,
} from 'shared/constants/regex'
import { dateFormat } from 'shared/lib/dateFormat'
import { DatePickerWithLabel } from 'shared/ui/date-picker-label'
import { InputWithLabel } from 'shared/ui/input-label'
import { Paper } from 'shared/ui/paper'
import { PhoneInputWhitLabel } from 'shared/ui/phone-input-label'

import { InsurancePolicyholderData } from '../../../../model/types'
import { PolicyholderFormValues } from '../../lib/policyholder-form'
import { CitizenshipField } from '../citizenship-field'
import { PersonActions } from '../person-actions'

import s from './styles.module.scss'

interface PolicyholderCardProps {
  control: Control<PolicyholderFormValues>
  errors?: FieldErrors<InsurancePolicyholderData>
  isSelf: boolean
  onSelectCitizenship: () => void
  onCloseCircle: () => void
  onScanPassport: () => void
}

export const PolicyholderCard: FC<PolicyholderCardProps> = ({
  control,
  errors,
  isSelf,
  onSelectCitizenship,
  onCloseCircle,
  onScanPassport,
}) => {
  const { t } = useTranslation()

  return (
    <Paper
      className={s.card}
      padding={[20, 16]}
    >
      <div className={s.header}>
        <h1 className={clsx(s.title, 'bold-17')}>
          {t(
            isSelf
              ? 'insurance.vzr.policyholder.your_data'
              : 'insurance.vzr.policyholder.participant',
            { count: 1 },
          )}
        </h1>
        <p className={clsx(s.subtitle, 'medium-12')}>
          {t(
            isSelf
              ? 'insurance.vzr.policyholder.edit_hint'
              : 'insurance.vzr.policyholder.fill_hint',
          )}
        </p>
      </div>

      <PersonActions
        onCloseCircle={onCloseCircle}
        onScanPassport={onScanPassport}
      />

      <div className={s.fields}>
        <Controller
          name="policyholder.lastName"
          control={control}
          render={({ field }) => (
            <InputWithLabel
              ref={field.ref}
              className={s.input}
              label={t('form.last_name')}
              error={errors?.lastName}
              autoComplete="family-name"
              autoCapitalize="characters"
              value={field.value.toUpperCase()}
              onBlur={field.onBlur}
              onChange={(event) =>
                field.onChange(
                  event.currentTarget.value
                    .toUpperCase()
                    .replace(REGEX_NAME, ''),
                )
              }
            />
          )}
        />
        <Controller
          name="policyholder.firstName"
          control={control}
          render={({ field }) => (
            <InputWithLabel
              ref={field.ref}
              className={s.input}
              label={t('form.first_name')}
              error={errors?.firstName}
              autoComplete="given-name"
              autoCapitalize="characters"
              value={field.value.toUpperCase()}
              onBlur={field.onBlur}
              onChange={(event) =>
                field.onChange(
                  event.currentTarget.value
                    .toUpperCase()
                    .replace(REGEX_NAME, ''),
                )
              }
            />
          )}
        />
        <Controller
          name="policyholder.inn"
          control={control}
          render={({ field }) => (
            <InputWithLabel
              ref={field.ref}
              className={s.input}
              label={t('insurance.vzr.policyholder.fields.inn')}
              error={errors?.inn}
              inputMode="numeric"
              value={field.value}
              onBlur={field.onBlur}
              onChange={(event) =>
                field.onChange(
                  event.currentTarget.value.replace(NON_DIGITS_REGEX, ''),
                )
              }
            />
          )}
        />

        <div className={s.field}>
          <Controller
            name="policyholder.birthDate"
            control={control}
            render={({ field }) => (
              <DatePickerWithLabel
                ref={field.ref}
                label={t('insurance.vzr.policyholder.fields.birth_date')}
                value={field.value ? dayjs(field.value).toDate() : undefined}
                error={errors?.birthDate}
                onChange={(date) =>
                  field.onChange(
                    dateFormat(date, DATE_FORMAT_LIST.YEAR_FULL_DATE),
                  )
                }
              />
            )}
          />
        </div>

        <Controller
          name="policyholder.passportNumber"
          control={control}
          render={({ field }) => (
            <InputWithLabel
              ref={field.ref}
              className={s.input}
              label={t('insurance.vzr.policyholder.fields.passport')}
              error={errors?.passportNumber}
              autoCapitalize="characters"
              maxLength={12}
              value={field.value.toUpperCase()}
              onBlur={field.onBlur}
              onChange={(event) =>
                field.onChange(
                  event.currentTarget.value
                    .toUpperCase()
                    .replace(REGEX_EMAIL_NUMBER, ''),
                )
              }
            />
          )}
        />
        <div className={s.field}>
          <Controller
            name="policyholder.citizenship"
            control={control}
            render={({ field, fieldState }) => (
              <CitizenshipField
                label={t('insurance.vzr.policyholder.fields.citizenship')}
                value={field.value?.name}
                error={fieldState.error}
                onSelect={onSelectCitizenship}
              />
            )}
          />
        </div>

        <div className={s.field}>
          <Controller
            name="policyholder.phone"
            control={control}
            render={({ field }) => (
              <PhoneInputWhitLabel
                ref={field.ref}
                value={field.value}
                error={errors?.phone}
                label={t('insurance.vzr.policyholder.fields.phone')}
                onChange={field.onChange}
              />
            )}
          />
        </div>
      </div>
    </Paper>
  )
}
